import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Wrench, 
  Plus, 
  Upload,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Camera,
  X
} from 'lucide-react';
import { tenantPortalAPI, maintenanceAPI } from '../services/api';

const TenantMaintenance = () => {
  const { orgId } = useParams();
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general_maintenance',
    priority: 'medium'
  });
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');

  const authed = !!(localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token'));

  const loadMyRequests = async () => {
    try {
      const data = await tenantPortalAPI.myMaintenance();
      setRequests(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load requests');
    }
  };

  useEffect(() => {
    if (authed) loadMyRequests();

  }, [authed]);

  const categories = [
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'hvac', label: 'HVAC/Heating' },
    { value: 'appliances', label: 'Appliances' },
    { value: 'pest_control', label: 'Pest Control' },
    { value: 'locks_security', label: 'Locks/Security' },
    { value: 'general_maintenance', label: 'General Maintenance' },
    { value: 'emergency', label: 'Emergency' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', description: 'Can wait a few days' },
    { value: 'medium', label: 'Medium', description: 'Should be addressed within a week' },
    { value: 'high', label: 'High', description: 'Needs immediate attention' }
  ];

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({ file, preview: URL.createObjectURL(file), name: file.name }));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => {
      const out = [...prev];
      URL.revokeObjectURL(out[index].preview);
      out.splice(index, 1);
      return out;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Create maintenance request (tenant-scoped)
      const created = await tenantPortalAPI.submitMaintenance({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
      });
      // Upload images if any
      if (selectedImages.length > 0 && created && (created._id || created.id)) {
        const id = created._id || created.id;
        for (const img of selectedImages) {
          try {
            await maintenanceAPI.uploadImage(id, img.file);
          } catch (e) {
            console.warn('Image upload failed for one file', e);
          }
        }
      }
      setShowNewRequestForm(false);
      setFormData({ title: '', description: '', category: 'general_maintenance', priority: 'medium' });
      setSelectedImages([]);
      await loadMyRequests();
    } catch (e) {
      setError(e.message || 'Failed to submit request');
    }
  };

  const RequestCard = ({ request }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{request.title}</h3>
            <p className="text-sm text-gray-600">Unit: {request.unitNumber || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-2">
            <span className={`text-xs px-2 py-1 rounded bg-gray-100`}>{request.status?.replace('_',' ')}</span>
            <span className={`text-xs px-2 py-1 rounded bg-gray-100 capitalize`}>{request.priority}</span>
          </div>
        </div>
        <p className="text-gray-700 mb-4">{request.description}</p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Submitted: {request.submitted_date ? new Date(request.submitted_date).toLocaleString() : '-'}</span>
          </div>
          {request.scheduled_date && (
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Scheduled: {new Date(request.scheduled_date).toLocaleString()}</span>
            </div>
          )}
        </div>
        {Array.isArray(request.images) && request.images.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Images:</p>
            <div className="flex space-x-2">
              {request.images.map((image, index) => (
                <img key={index} src={image} alt={`Request ${index+1}`} className="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80" onClick={()=> window.open(image,'_blank')} />
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">Category: <span className="capitalize font-medium">{(request.category || '').replace('_',' ')}</span></div>
          {request.status === 'completed' && request.completed_date && (
            <div className="text-sm text-green-600">Completed: {new Date(request.completed_date).toLocaleString()}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (!authed) {
    return (
      <div className="p-6 space-y-4">
        <div className="rounded-xl bg-yellow-50 p-4 text-yellow-800">Please sign in via the Tenant Hub to submit and view your requests.</div>
        <Link to={`/o/${orgId}/tenant`} className="inline-block"><Button>Go to Tenant Hub</Button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Work Orders</h1>
          <p className="text-gray-600 mt-1">Submit and track your work orders</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowNewRequestForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> New Request
        </Button>
      </div>

      {/* New Request Form Modal */}
      {showNewRequestForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Submit Work Order</CardTitle>
              <Button variant="ghost" onClick={() => setShowNewRequestForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="title">Request Title *</Label>
                  <Input id="title" value={formData.title} onChange={(e)=>setFormData({...formData, title: e.target.value})} required />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select id="category" value={formData.category} onChange={(e)=>setFormData({...formData, category: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500" required>
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Priority *</Label>
                  <div className="space-y-2">
                    {priorities.map(p => (
                      <label key={p.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input type="radio" name="priority" value={p.value} checked={formData.priority===p.value} onChange={(e)=>setFormData({...formData, priority: e.target.value})} />
                        <div>
                          <div className="font-medium">{p.label}</div>
                          <div className="text-sm text-gray-600">{p.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Detailed Description *</Label>
                  <Textarea id="description" rows="4" value={formData.description} onChange={(e)=>setFormData({...formData, description: e.target.value})} required />
                </div>
                <div>
                  <Label>Photos (Optional)</Label>
                  <div className="mt-2">
                    <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
                    <label htmlFor="image-upload" className="flex items-center justify-center w-full p-4 border-2 border-dashed rounded-lg hover:border-gray-400 cursor-pointer">
                      <div className="text-center">
                        <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Click to add photos</p>
                        <p className="text-xs text-gray-500">JPG, PNG up to 10MB each</p>
                      </div>
                    </label>
                  </div>
                  {selectedImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      {selectedImages.map((image, index) => (
                        <div key={index} className="relative">
                          <img src={image.preview} alt={image.name} className="w-full h-24 object-cover rounded-lg" />
                          <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {error && <div className="text-sm text-red-600">{error}</div>}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowNewRequestForm(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Submit Request</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-6">
        {requests && requests.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {requests.map((request) => (
              <RequestCard key={request._id || request.id} request={request} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Wrench className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No work orders</h3>
              <p className="text-gray-600 mb-4">You haven't submitted any work orders yet.</p>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowNewRequestForm(true)}>
                <Plus className="h-4 w-4 mr-2" /> Submit Your First Request
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle>Emergency Contacts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
              <h4 className="font-semibold text-red-900">Emergency</h4>
              <p className="text-sm text-red-700">Call 911 for immediate emergencies</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Wrench className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-blue-900">Property Manager</h4>
              <p className="text-sm text-blue-700">(555) 123-0000</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <Clock className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-semibold text-green-900">Office Hours</h4>
              <p className="text-sm text-green-700">Mon-Fri: 9AM-5PM</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantMaintenance;